// Utilitas format & ID — diport dari prototype v0.18 (perilaku sama).
const digits = (s: unknown): string => String(s ?? '').replace(/\D/g, '');

export const rp = (n: number): string => 'Rp ' + (n || 0).toLocaleString('id-ID');

/** Format input jadi "Rp 1.234" (untuk field uang oninput). */
export const rpFmt = (s: string): string => {
  const d = digits(s);
  return d ? 'Rp ' + (+d).toLocaleString('id-ID') : 'Rp 0';
};

/** Ambil angka murni dari string ber-Rp. */
export const rpVal = (s: string): number => +digits(s) || 0;

/** Isi template pesan: ganti {key} dengan vars[key] (tak ada → string kosong). Dipakai Template WA. */
export const fillTpl = (tpl: string, vars: Record<string, string>): string =>
  String(tpl ?? '').replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? '');

/** Parse nilai komisi locale-ID: titik=ribuan, koma=desimal. "5.000"→5000, "10,5"→10.5, ""→0.
 * Dipakai skema komisi per-karyawan (nominal Rp/unit maupun % profit/omzet). */
export const parseKomisiVal = (s: string): number => {
  const n = parseFloat(String(s ?? '').replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** Normalisasi nomor jadi format 62… (WhatsApp). */
export function wa62(v: string): string {
  let d = digits(v);
  if (!d) return '';
  if (d.startsWith('0')) d = '62' + d.slice(1);
  else if (!d.startsWith('62')) d = '62' + d;
  // "+62" diikuti "0" (mis. pelanggan ketik +62 lalu 0852…) → buang 0 di awal bagian lokal:
  // 6208…→628…. Nomor seluler Indonesia sesudah 62 selalu 8x, jadi 0 di sini pasti salah-ketik.
  d = '62' + d.slice(2).replace(/^0+/, '');
  return d;
}

/** Sensor No. HP untuk tampilan semi-publik (tracking): 2 depan + *** + 4 belakang (spec v0.18). */
export function maskPhone(v: string | null | undefined): string {
  const d = digits(v ?? '');
  if (!d) return '-';
  if (d.length < 6) return d;
  return d.slice(0, 2) + '***' + d.slice(-4);
}

const M = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

/** ISO tanggal (YYYY-MM-DD) berbasis kalender LOKAL (bukan UTC). */
export const isoOf = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const todayISO = (): string => isoOf(new Date());

export const tglHuman = (d = new Date()): string => `${d.getDate()} ${M[d.getMonth()]} ${d.getFullYear()}`;

/** "2026-08-02" → "2 Agu 2026". Kosong/rusak → string kosong (jangan cetak "Invalid Date"). */
export const tglID = (iso: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
  return m ? `${+m[3]!} ${M[+m[2]! - 1]} ${m[1]}` : '';
};

export function nowStr(): string {
  const d = new Date();
  return `${tglHuman(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Tanggal+jam LOKAL dari ISO string tersimpan (created_at/serah.at). '—' bila kosong/invalid. */
export const dtLocal = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—'
    : `${tglHuman(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Jadikan nomor tiket di TEKS (sudah di-escape) bisa diklik → detail tiket.
 *  Handler klik global .tno-link ada di shell — anchor ini otomatis hidup di mana pun (termasuk modal).
 *  Pola ketat OUTLET-yymmdd-XXXX agar kata biasa tak ikut ter-link. */
export const linkifyNos = (escapedHtml: string): string =>
  escapedHtml.replace(/\b[A-Z]{2,6}-\d{6}-[A-Z0-9]{4}\b/g,
    (no) => `<a class="tno-link" data-no="${no}" title="Buka detail tiket">${no}</a>`);

/** Pecah "Samsung A54" → {merk:'Samsung', tipe:'A54'} untuk pesan WA berbaris.
 *  Dipakai layar yang hanya menyimpan gabungan unit; sumber yang punya kolom terpisah tak perlu ini. */
export const pecahUnit = (unit: string): { merk: string; tipe: string } => {
  const t = (unit ?? '').trim();
  const sp = t.indexOf(' ');
  return sp < 0 ? { merk: t, tipe: '' } : { merk: t.slice(0, sp), tipe: t.slice(sp + 1) };
};

/**
 * Pengacak untuk kode yang dipakai sebagai KUNCI (token tracking) atau identitas (nomor servis).
 *
 * `Math.random()` tidak dipakai: keluarannya deret yang bisa direkonstruksi dari beberapa nilai
 * sebelumnya, dan token tracking adalah satu-satunya kunci untuk membuka status, garansi, dan
 * survei sebuah tiket. Seseorang yang menyerahkan dua-tiga unit berturut-turut lalu mencatat
 * tokennya tak boleh punya cara menebak token pelanggan berikutnya.
 *
 * `crypto.getRandomValues` tersedia di semua peramban yang menjalankan aplikasi ini (dan di
 * Node ≥ 19 lewat globalThis.crypto), jadi tak ada jalur cadangan yang perlu dijaga.
 * Ditolak lewat modulo bias: 256 dibagi rata terhadap panjang alfabet dengan menolak sisa atas.
 */
function kodeAcak(alfabet: string, panjang: number): string {
  const batas = Math.floor(256 / alfabet.length) * alfabet.length;
  let out = '';
  const buf = new Uint8Array(panjang * 2);
  while (out.length < panjang) {
    crypto.getRandomValues(buf);
    for (const b of buf) {
      if (b >= batas) continue;              // buang agar tiap huruf berpeluang sama
      out += alfabet[b % alfabet.length];
      if (out.length === panjang) break;
    }
  }
  return out;
}

/** Token opaque 12-char untuk link tracking publik. */
export function genToken(): string {
  return kodeAcak('abcdefghijkmnpqrstuvwxyz23456789', 12);
}

/** Nomor servis acak anti-pemalsuan: OUTLET-yymmdd-XXXX (tanpa 0/O/1/I). */
export function genNo(outlet: string): string {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${outlet}-${yy}${mm}${dd}-${kodeAcak('ABCDEFGHJKLMNPQRSTUVWXYZ23456789', 4)}`;
}

/**
 * Nomor dokumen internal (beban, dsb): PREFIX-yymmdd-XXXX.
 * Bentuknya sama dengan nomor servis supaya orang tak perlu belajar dua pola, dan acak
 * supaya dua orang yang mencatat pada detik yang sama tak pernah mendapat nomor yang sama.
 */
export function genNoDok(prefix: string): string {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  // tanpa 0/O/1/I — sering salah dibaca di kertas
  return `${prefix}-${yy}${mm}${dd}-${kodeAcak('ABCDEFGHJKLMNPQRSTUVWXYZ23456789', 4)}`;
}

export const cleanText = (s: unknown, max = 1000): string =>
  String(s ?? '').replace(/[<>]/g, '').trim().slice(0, max);

/** Escape untuk render aman ke HTML. */
export const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/** Umur (hari) sejak tanggal ISO. */
export const ageDays = (iso: string | null | undefined): number =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : 0;
